import { FormEvent, useCallback, useState } from 'react'
import { connect } from 'react-redux'
import { renderMarkup } from 'react-render-markup'
import { Dispatch } from '@reduxjs/toolkit'
import { TdsField } from '@trv-tds/react'

import { photoCaptureHelp, supplementRequestsHelp } from '../shared/constants'
import { WrappedTdsModal } from '../shared/TdsModal'
import type { GlobalState } from '../types'
import type { CategorySelectProps } from './types'
import { uploadClear,uploadSelectCategory } from './upload-actions'
import {
  categories,
  categoriesMedical,
  roleAutoAppraisal,
  roleMedical
} from './upload-constants'
import { getClaimFormValues, getClaimHandlers, getIdentityValues } from './upload-selectors'

const CategorySelectComponent = (props: CategorySelectProps) => {
  const {
    claim,
    claimForm,
    onSelectCategory,
    finished,
    onClear,
    category,
    requesting
  } = props
  const [displayModal, setDisplayModal] = useState<boolean>(false)
  const [isSupplement, setIsSupplement] = useState<boolean>(false)
  const [modalText, setModalText] = useState<JSX.Element>(<span />)
  const [modalTitle, setModalTitle] = useState<string | undefined>(undefined)
  const [modalLabel, setModalLabel] = useState<string>('')

  const matchRoleCategories = useCallback((
    category: string,
    claimForm: { roleInClaim: string }
  ) => {
    return claim.lob.toUpperCase() === 'A'
      && categoriesMedical.includes(category)
      && roleMedical.includes(claimForm.roleInClaim)
  }, [claim])

  const matchAutoPhotoCategories = useCallback(
    (category: string, claimForm: { roleInClaim: string }) => {
      return claim.lob.toUpperCase() === 'A'
        && category === 'damageAutoHomeBus'
        && roleAutoAppraisal.includes(claimForm.roleInClaim)
    },
    [claim]
  )

  const matchAutoApprRoleCategories = useCallback(
    (category: string, claimForm: { roleInClaim: string }) => {
    return claim.lob.toUpperCase() === 'A'
      && category === 'estimatesAppraisals'
        && roleAutoAppraisal.includes(claimForm.roleInClaim)
    },
    [claim]
  )


  const onCategorySelect = useCallback(
    (event: FormEvent<HTMLSelectElement>) => {
      setIsSupplement(false)
      setModalTitle(undefined)
      // format to reflect MM/DD/YYYY format
      const formattedDateOfLost = !claim.dateOfLoss
        ? ''
        : claim.dateOfLoss.slice(0,2) +
          '/' + claim.dateOfLoss.slice(2,4) + '/' +
          claim.dateOfLoss.slice(-4)
      // eslint-disable-next-line max-len
      const medicalHelp = `<p>Medical providers should not use this process.  Use eBill or, for more information on how to submit medical bills or reports for consideration under Auto Medical Payments or Personal Injury Protection coverages, please contact the Provider Inquiry Center at 877-838-7281 or <a href="mailto:providerinquiry@travelers.com?subject=${'Claim Number ' + claim.claim + ' – DOL ' + formattedDateOfLost + ' – Upload Center Inquiry'}">providerinquiry@travelers.com</a>.</p>`
      onSelectCategory(event.currentTarget.value)
      if (matchRoleCategories(event.currentTarget.value, claimForm)) {
        setDisplayModal(true)
        setModalText(renderMarkup(medicalHelp))
        setModalLabel('Auto Medical Warning')
      } else if (matchAutoApprRoleCategories(event.currentTarget.value, claimForm)) {
        setDisplayModal(true)
        setIsSupplement(true)
        setModalText(renderMarkup(supplementRequestsHelp))
        setModalTitle('Supplement Requests')
        setModalLabel('Auto Appraisal Warning')
      } else if (matchAutoPhotoCategories(event.currentTarget.value, claimForm)) {
        setDisplayModal(true)
        setModalText(renderMarkup(photoCaptureHelp))
        setModalTitle('Photo Capture')
        setModalLabel('Auto Photo Warning')
      }
    },
    [
      claim,
      claimForm,
      onSelectCategory,
      matchAutoApprRoleCategories,
      matchRoleCategories,
      matchAutoPhotoCategories
    ]
  )

  const clearFiles = useCallback(() => {
    if (finished) {
      onClear()
    }
  }, [finished, onClear])

  return (
    <TdsField label='What are you uploading?' style={{ width: '50%' }}>
      <select
        name='category'
        id='categoryselection'
        onChange={onCategorySelect}
        onClick={clearFiles}
        disabled={requesting}
        value={category}
      >
        <option value="">Select Option</option>
        {categories.reduce<React.ReactElement<'option'>[]>((acc, option, index) => {
          // Only display wage information for Worker's Comp claims.
          if (claim.lob !== 'W' && option.value === 'wageInformation') return acc
          acc.push(<option key={index} value={option.value}>{option.display}</option>)
          return acc
        }, [])}
      </select>
      {
        displayModal ?
          <WrappedTdsModal
            size='medium'
            buttonText={isSupplement ? 'Go to Supplement Portal' : 'Okay'}
            handleOk={
              isSupplement
              ? () => window.open('https://supplements.travelers.com/', '_self')
              : undefined
            }
            onTdsClose={() => setDisplayModal(false)}
            label={modalLabel}
            hasCancelButton={false}
            modalTitle={modalTitle}
          >
            {modalText}
          </WrappedTdsModal>
          : null
      }
    </TdsField>
  )
}

const mapStateToProps = (state: GlobalState) => ({
  category: state.upload.category,
  finished: state.upload.finished,
  requesting: state.upload.requesting,
  claimHandlers: getClaimHandlers(state),
  claim: state.claim.claimDetails,
  claimForm: getClaimFormValues(state),
  identity: getIdentityValues(state)
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onSelectCategory: (category: string) => dispatch(uploadSelectCategory(category)),
  onClear: () => dispatch(uploadClear()),
})

const CategorySelect = connect(mapStateToProps, mapDispatchToProps)(CategorySelectComponent)

export {
  CategorySelect,
  CategorySelectComponent // for testing without HOC
}
